import React, { useEffect, useRef, useState, useContext } from 'react'
// @ts-ignore
import { Janus } from 'janus-gateway'
import {
  AddStreamOptions,
  ConstructorOptions,
  VideoStreamMerger,
} from 'video-stream-merger'
import {
  setDisplayMedia,
  removeDisplayMedia,
  setUserMedia,
} from '../utils/mediaDevices'
import Panel from './Panel'
import LocalVideo from './LocalVideo'
import Loading from '../components/Loading'
import { message } from 'antd'
import { useVideoStore } from '../VideoSetting'
import { JanusWebsocketContext } from '../service/JanusWebsocketConn'
import * as Sentry from '@sentry/react'
import classNames from 'classnames'
import { VideoContext } from '~/service/videoConn'

type Props = {
  server: string
  videoId: string
  room: number
  pin: string
  token: string
  type: string
  userMedia: any
}

const Main: React.FC<Props> = ({
  server,
  videoId,
  room,
  pin,
  token,
  type,
  userMedia,
}) => {
  const {
    audio,
    video,
    screen,
    webcamAccess,
    audioAccess,
    videoWidth,
    videoHeight,
    small,
    setScreen,
    setSmall,
  } = useVideoStore(state => ({
    audio: state.audio,
    video: state.video,
    screen: state.screen,
    webcamAccess: state.webcamAccess,
    audioAccess: state.audioAccess,
    videoWidth: state.videoWidth,
    videoHeight: state.videoHeight,
    small: state.small,
    setVideo: state.setVideo,
    setScreen: state.setScreen,
    setSmall: state.setSmall,
  }))
  const { leaveRoom } = useContext(VideoContext)
  const wsContext = useContext<any>(JanusWebsocketContext)
  const [guest, setGuest] = useState<any>('')
  const [guestAudio, setGuestAudio] = useState<boolean>(true)
  const [guestVideo, setGuestVideo] = useState<boolean>(true)
  const [guestScreen, setGuestScreen] = useState<boolean>(false)
  const [alert, setAlert] = useState<string>('')
  const janusInstance = useRef<any>(null)
  const publisher = useRef<any>(null)
  const displayMedia = useRef<any>(null)
  const mypvtid = useRef<any>(null)
  const guestScreenRef = useRef<boolean>(guestScreen)
  const webcamAccessRef = useRef<boolean>(webcamAccess)
  const audioAccessRef = useRef<boolean>(audioAccess)
  const remoteVideoRef = useRef<any>()
  const remoteAudioRef = useRef<any>()
  const localVideoRef = useRef<any>()

  guestScreenRef.current = guestScreen
  webcamAccessRef.current = webcamAccess
  audioAccessRef.current = audioAccess

  const merger = useRef(
    new VideoStreamMerger({
      width: videoWidth,
      height: videoHeight,
    } as ConstructorOptions)
  )

  // useEffect
  useEffect(() => {
    wsContext.channel.push('mute', {
      type: type,
      device: 'video',
      status: video && webcamAccess,
    })

    wsContext.channel.push('mute', {
      type: type,
      device: 'audio',
      status: audio && audioAccess,
    })

    navigator.mediaDevices.addEventListener('devicechange', async event => {
      if (userMedia.current) {
        await merger.current.removeStream(userMedia.current)
      }

      const videoBefore = webcamAccessRef.current
      const audioBefore = audioAccessRef.current

      userMedia.current = await setUserMedia()

      if (userMedia.current) {
        merger.current.addStream(userMedia.current, {
          x: 0,
          y: 0,
          width: merger.current.width,
          height: merger.current.height,
          mute: false,
          index: 0,
        } as AddStreamOptions)

        merger.current.setOutputSize(videoWidth, videoHeight)

        if (videoBefore != webcamAccessRef.current) {
          wsContext.channel.push('mute', {
            type: type,
            device: 'video',
            status: webcamAccessRef.current,
          })
        }

        if (audioBefore != audioAccessRef.current) {
          wsContext.channel.push('mute', {
            type: type,
            device: 'audio',
            status: audioAccessRef.current,
          })
        }

        if (!videoBefore) {
          setLocalStream(userMedia)
        }
      }
    })

    init()
  }, [])

  useEffect(() => {
    wsContext.channel.on('mute', ({ device, status }) => {
      device == 'audio' ? setGuestAudio(status) : setGuestVideo(status)
    })

    wsContext.channel.on('guest_screen', ({ status }) => {
      setGuestScreen(status)
    })

    wsContext.channel.on('devices_status', ({ audio, video }) => {
      setGuestAudio(audio)
      setGuestVideo(video)
    })
  }, [])

  // Janus
  const init = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      Janus.init({
        debug: process.env.APP_ENV == 'dev' ? 'all' : false,
        callback: () => {
          const janus = new Janus({
            server: server,
            token: token,
            success: () => {
              janusInstance.current = janus

              publish()
              resolve()
            },
            error: (err: string) => {
              Sentry.captureException(err)
              console.error('Janus Initialization failed! Exiting...', err)
              reject()
            },
          })
        },
      })
    })
  }

  const publish = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      janusInstance.current!.attach!({
        plugin: 'janus.plugin.videoroom',
        opaqueId: videoId,
        success: (pluginHandle: any) => {
          publisher.current = pluginHandle
          // Request to join the room
          let request: { [key: string]: any } = {
            request: 'join',
            room: room,
            ptype: 'publisher',
            token: token,
            pin: pin,
          }
          pluginHandle.send({ message: request })
        },
        onmessage: async (msg: any, jsep: string) => {
          let event = msg['videoroom']
          Janus.log('----------- msg ----------', msg)

          if (event) {
            if (event === 'joined') {
              wsContext.channel.push('joined', {
                session: janusInstance.current.getSessionId(),
                handle: publisher.current.id,
              })

              Janus.log('--- session', janusInstance.current.getSessionId())
              Janus.log('--- handle', publisher.current.id)

              mypvtid.current = msg['private_id']

              await publishOwnFeed()

              if (msg['publishers']) {
                const list = msg['publishers']
                Janus.log('---- joined publishers ----', list)

                if (list[0] && list.length <= 1) {
                  const id = list[0]['id']
                  const streams = list[0]['streams']

                  for (let i in streams) {
                    let stream = streams[i]
                    stream['id'] = id
                  }

                  newRemoteFeed(id, streams)
                }
              }
            } else if (event === 'destroyed') {
              Janus.log('destroyed')
              message.info(intl.formatMessage({ id: 'janus.alert.leave' }))
              removeStreams()
              leaveRoom()
              setSmall(false)
            } else if (event === 'event') {
              Janus.log('event event')
              Janus.log(msg)
              if (msg['publishers']) {
                const list = msg['publishers']
                Janus.log('---- event publishers ----', list)

                if (list[0] && list.length <= 1) {
                  const id = list[0]['id']
                  const streams = list[0]['streams']

                  for (var i in streams) {
                    var stream = streams[i]
                    stream['id'] = id
                  }

                  newRemoteFeed(id, streams)
                }
              } else if (msg['leaving']) {
                Janus.log('leaving')
                // setAlert('Rozmowa została zakończona')
                // removeStreams()
                message.warning(
                  intl.formatMessage({ id: 'janus.message.leaving' })
                )
                setGuest('')
              } else if (msg['unpublished']) {
                // One of the publishers has unpublished?
                var unpublished = msg['unpublished']
                Janus.log('Publisher left: ' + unpublished)
              } else if (msg['error']) {
                Sentry.captureException(msg['error'])

                if (msg['error_code'] === 426) {
                  // This is a "no such room" error: give a more meaningful description
                  Janus.error(msg['error'])
                  setAlert(intl.formatMessage({ id: 'janus.alert.leave' }))
                  message.info(intl.formatMessage({ id: 'janus.alert.leave' }))
                  removeStreams()
                  leaveRoom()
                  setSmall(false)
                } else {
                  Janus.error(msg['error'])
                  setAlert(msg['error'])
                  removeStreams()
                }
              }
            }
          }
          if (jsep) {
            publisher.current.handleRemoteJsep({ jsep: jsep })
          }
        },
        onlocaltrack: async (track: any, on: any) => {
          Janus.debug('Local track ' + (on ? 'added' : 'removed') + ':', track)

          setLocalStream(userMedia)
        },
        mediaState: (medium, on, mid) => {
          Janus.log(
            'Janus ' +
              (on ? 'started' : 'stopped') +
              ' receiving our ' +
              medium +
              ' (mid=' +
              mid +
              ')'
          )

          // if (blurStatusRef.current && mid == 0) {
          //   setBlurStream()
          //   setLocalStream(blurStream)
          // }
        },
        error: (err: string) => {
          Janus.log('Publish: Janus VideoRoom Plugin Error!')
          Sentry.captureException(err)
          Janus.log(err)
          reject()
        },
      })
    })
  }

  const publishOwnFeed = async () => {
    await setStream()

    publisher.current.createOffer({
      tracks: [
        {
          type: 'video',
          recv: false,
          capture: merger.current.result?.getVideoTracks()[0],
        },
        {
          type: 'audio',
          recv: false,
          capture: merger.current.result?.getAudioTracks()[0],
        },
      ],

      success: (jsep: string) => {
        const publish = {
          request: 'configure',
          audio: true,
          video: true,
        }
        publisher.current.send({ message: publish, jsep: jsep })
        !video && publisher.current.muteVideo()
        !audio && publisher.current.muteAudio()
      },
      error: (error: any) => {
        Janus.log('WebRTC error... ')
        Sentry.captureException(error)
        publishOwnFeed()
      },
    })
  }

  const newRemoteFeed = (id: number, streams: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      let remoteFeed: any = null

      janusInstance.current!.attach!({
        plugin: 'janus.plugin.videoroom',
        opaqueId: videoId,

        success: (pluginHandle: any) => {
          remoteFeed = pluginHandle
          remoteFeed.remoteTracks = {}
          remoteFeed.remoteVideos = 0
          remoteFeed.simulcastStarted = false

          var subscription: any = []

          for (var i in streams) {
            var stream = streams[i]
            // If the publisher is VP8/VP9 and this is an older Safari, let's avoid video
            if (
              stream.type === 'video' &&
              Janus.webRTCAdapter.browserDetails.browser === 'safari' &&
              (stream.codec === 'vp9' ||
                (stream.codec === 'vp8' && !Janus.safariVp8))
            ) {
              Janus.log(
                'Publisher is using ' +
                  stream.codec.toUpperCase +
                  ", but Safari doesn't support it: disabling video stream #" +
                  stream.mindex
              )
              continue
            }
            subscription.push({
              feed: stream.id, // This is mandatory
              mid: stream.mid, // This is optional (all streams, if missing)
            })
            // FIXME Right now, this is always the same feed: in the future, it won't
          }

          const subscribe = {
            request: 'join',
            room: room,
            ptype: 'subscriber',
            streams: subscription,
            private_id: mypvtid.current,
            pin: pin,
          }

          remoteFeed.send({ message: subscribe })
          setGuest(id)
          message.success(intl.formatMessage({ id: 'janus.message.join' }))
        },
        onmessage: async (msg: any, jsep: string) => {
          if (jsep) {
            Janus.debug('Handling SDP as well...', jsep)
            // Answer and attach
            remoteFeed.createAnswer({
              jsep: jsep,
              // We only specify data channels here, as this way in
              // case they were offered we'll enable them. Since we
              // don't mention audio or video tracks, we autoaccept them
              // as recvonly (since we won't capture anything ourselves)
              tracks: [{ type: 'data' }],
              success: function (jsep: any) {
                var body = { request: 'start', room: room }
                remoteFeed.send({ message: body, jsep: jsep })
              },
              error: function (error: any) {
                Sentry.captureException(error)
                Janus.log('WebRTC error:')
                Janus.log(error)
              },
            })
          }
        },
        onremotetrack: async (track: any, mid: any, on: any) => {
          if (track.kind === 'audio') {
            let stream = new MediaStream([track])
            remoteFeed.remoteTracks[mid] = stream

            await Janus.attachMediaStream(remoteAudioRef.current, stream)
          }

          if (track.kind === 'video') {
            remoteFeed.remoteVideos++
            let stream = new MediaStream([track])
            remoteFeed.remoteTracks[mid] = stream

            await Janus.attachMediaStream(remoteVideoRef.current, stream)
          }

          remoteVideoRef.current.onplaying = () => {
            wsContext.channel.push('check_stream', {})
          }
        },
        error: (err: string) => {
          Sentry.captureException(err)
          Janus.log('Publish: Janus VideoRoom Plugin Error!')
          Janus.log(err)
          reject()
        },
      })
    })
  }

  // Stream configure
  const setStream = async () => {
    merger.current.start()
    userMedia.current &&
      merger.current.addStream(userMedia.current, {
        x: 0,
        y: 0,
        width: merger.current.width,
        height: merger.current.height,
        mute: false,
        index: 0,
      } as AddStreamOptions)
  }

  const setScreensharing = async () => {
    displayMedia.current = await setDisplayMedia()
    // await merger.current.setOutputSize(1920, 1080)
    await merger.current.setOutputSize(1600, 900)
    merger.current.addStream(displayMedia.current, {
      x: 0,
      y: 0,
      width: merger.current.width,
      height: merger.current.height,
      mute: true,
      index: 2,
    } as AddStreamOptions)

    wsContext.channel.push('screensharing', { status: true })

    if (!webcamAccess || !video) {
      setTimeout(() => {
        wsContext.channel.push('mute', {
          type: type,
          device: 'video',
          status: true,
        })

        publisher.current.unmuteVideo()
        // setVideo(true)
      }, 1000)
    }

    displayMedia.current
      .getVideoTracks()[0]
      .addEventListener('ended', async () => {
        await removeScreensharing()

        setLocalStream(userMedia)

        wsContext.channel.push('screensharing', { status: false })
      })

    setScreen(true)
  }

  const removeScreensharing = async () => {
    if (displayMedia.current) {
      merger.current.removeStream(displayMedia.current)
      removeDisplayMedia(displayMedia.current)
      merger.current.setOutputSize(videoWidth, videoHeight)
      wsContext.channel.push('screensharing', { status: false })
      // console.log('======', videoWidth)
      // console.log('======', videoHeight)
    }

    if (!webcamAccess || !video) {
      publisher.current.muteVideo()

      wsContext.channel.push('mute', {
        type: type,
        device: 'video',
        status: false,
      })
    }

    if (video) {
      publisher.current.unmuteVideo()

      wsContext.channel.push('mute', {
        type: type,
        device: 'video',
        status: true,
      })

      reloadLocalStream()
    }

    setScreen(false)
  }

  const removeStreams = () => {
    userMedia.current && removeDisplayMedia(userMedia.current)
    displayMedia.current && removeDisplayMedia(displayMedia.current)
  }

  const setLocalStream = stream => {
    if ('srcObject' in localVideoRef.current) {
      localVideoRef.current.srcObject = stream.current
    } else {
      localVideoRef.current.src = window.URL.createObjectURL(stream.current)
    }
  }

  const reloadLocalStream = () => {
    setLocalStream(userMedia)
  }

  // Controls
  const toggleScreensharing = async () => {
    if (screen) {
      await removeScreensharing()
      setLocalStream(userMedia)
    } else {
      await setScreensharing()
      setLocalStream(displayMedia)
    }
  }

  return (
    <>
      {!alert ? (
        <>
          <div className="janus__main">
            <button
              onClick={() => setSmall(!small)}
              className={classNames('janus__button', {
                'janus__button--min': !small,
                'janus__button--max': small,
              })}
            >
              {small ? (
                <i className="fa-solid fa-expand"></i>
              ) : (
                <i className="fa-solid fa-minus"></i>
              )}
            </button>
            {!guest && <Loading />}
            <LocalVideo localVideoRef={localVideoRef} />
            <div
              className={classNames('janus__remote', {
                'janus__remote--screen': guestScreen,
              })}
            >
              <audio
                ref={remoteAudioRef}
                className="janus__remote-audio"
                id="remoteaudio"
                autoPlay
                playsInline
              />
              <video
                ref={remoteVideoRef}
                // width={1024}
                // height={576}
                // width={1280}
                // height={720}
                // width={1920}
                // height={1080}
                className={
                  guestScreen ? 'janus__remote-screen' : 'janus__remote-video'
                }
                id="remotevideo"
                autoPlay
                playsInline
              />
              {guest && !guestAudio && guestVideo && (
                <div>
                  <i className="fas fa-microphone-slash janus__remote-icon janus__remote-icon--video"></i>
                </div>
              )}
            </div>
            {guest && !guestVideo && !guestScreen && (
              <div className="janus__remote-avatar">
                {!guestAudio && (
                  <i className="fas fa-microphone-slash janus__remote-icon"></i>
                )}
                <i className="fas fa-user"></i>
              </div>
            )}
          </div>
          <Panel
            publisher={publisher}
            toggleScreensharing={toggleScreensharing}
            type={type}
            setAlert={setAlert}
            reloadLocalStream={reloadLocalStream}
          />
        </>
      ) : (
        <div className="janus__alert">
          {alert} <i className="fas fa-phone-slash"></i>
        </div>
      )}
    </>
  )
}

export default Main
